import { config } from '@/lib/config';
import { log } from '@/lib/log';
/**
 * Legacy v1 MQTT source.
 *
 * Connects to the AirMarket v1 EMQX broker over WSS, subscribes to the
 * legacy topics we have payload samples for, and maps each incoming
 * message to the v2 envelope shape so the rest of the app (via
 * `MqttConnector`) sees the same `MqttSource` interface as the mock.
 *
 * Currently wired:
 *   - `networkid/<UAS_ID>`           → telemetry (primary stream per drone)
 *   - `traffic_control`              → backfill telemetry.status (re-emits telemetry)
 *   - `TISB Stream/<UAS_ID>`         → backfill c2.ec.tisb.status (emits minimal c2)
 *   - `surveillance_traffic/<emitter>` → targets (fused across emitters @ 1 Hz)
 *   - `flysafe/v1/.../telemetry`     → MAVLink demuxer; backfills battery,
 *                                       GPS sats, HDOP, fix, flight_mode,
 *                                       ops_status, signal, climbRate (4 Hz)
 *
 * Streams known but not yet wired:
 *   - `surveillance_sensors/#`       → surveillance
 *   - `flightplan/#`                 → OI polygons
 *   - `thing/product/<sn>/osd`       → dock OSD telemetry
 *   - `weather/<type>`               → wx store
 *
 * Backfill model
 * --------------
 * Several v1 topics carry partial state — `traffic_control` gives drone
 * status, `TISB Stream` gives TIS-B connection. The v2 store stores the
 * whole envelope per drone, not field-level. So this source keeps a
 * per-drone snapshot of the last emitted telemetry / c2 envelope and,
 * when a backfill update arrives, merges + re-emits the full envelope
 * with the patched field. This is the source's responsibility — the
 * store remains a simple "last writer wins per envelope kind" reducer.
 */
import mqtt, { type MqttClient } from 'mqtt';
import { parseDocAlert, parseDocEvent } from './legacy/eventsAlerts';
import { flightplanToOiUpdate } from './legacy/flightplan';
import {
  GCS_READ_TYPES,
  GCS_STREAM_FIELDS,
  GCS_TYPE_MIN_GAP_MS,
  gcsStreamToPartial,
  gcsStreamTopic,
  isGcsStreamTopic,
  peekGcsType,
} from './legacy/gcsStream';
import {
  LIVE_RF_EMITTER,
  isLiveRfTopic,
  liveRfKind,
  parseLiveRfHeartbeat,
  parseLiveRfRealtime,
} from './legacy/liveRf';
import { mavlinkToPartialTelemetry } from './legacy/mavlink';
import { networkidToOiUpdate, networkidToTelemetry } from './legacy/networkid';
import {
  type EmitterSnapshot,
  parseSurveillanceSensors,
  snapshotsToEnvelope,
} from './legacy/surveillanceSensors';
import {
  type PartialTarget,
  surveillanceTrafficToPartialTargets,
} from './legacy/surveillanceTraffic';
import { tisbStreamToUpdate } from './legacy/tisbStream';
import { trafficControlToUpdate } from './legacy/trafficControl';
import { weatherSensorsToEnvelope } from './legacy/weather';
import { hasHigherTrust, mergeNonNull, pickEmitter, reasonCodeText } from './mqttHelpers';
import { outputAlertToEnvelope, outputEventToEnvelope } from './output/events';
import { remoteIdToOiUpdate, remoteIdToTelemetry } from './output/remoteId';
import {
  outputTelemetryToC2Update,
  outputTelemetryToOiUpdate,
  outputTelemetryToPartial,
} from './output/telemetry';
import type { C2LinksEnvelope } from './schemas/c2links';
import type { OiLiveEnvelope } from './schemas/oi';
import { KNOWN_EMITTERS, isServiceEmitter } from './schemas/surveillance';
import type { Target, TargetsEnvelope } from './schemas/targets';
import type { TelemetryEnvelope } from './schemas/telemetry';
import type { WxSensorRecord } from './schemas/weather';
import { LEGACY_SUBSCRIPTIONS, WILDCARD_SUBSCRIPTIONS } from './subscriptions';
import { isPerFieldAliasTopic, isV1AlertTopic, isV1EventTopic, parseOutputTopic } from './topics';
import type {
  ConnectionState,
  MqttListener,
  MqttPayload,
  MqttPublishOptions,
  MqttSource,
  RawMqttListener,
  RawMqttMessage,
} from './types';

const RECONNECT_MS = 5_000;
const KEEPALIVE_S = 30;
const CONNECT_TIMEOUT_MS = 30_000;

const TARGETS_EMIT_MS = 1_000; // emit a unified targets envelope at 1 Hz
const SURVEILLANCE_EMIT_MS = 5_000; // emit a unified surveillance envelope at 0.2 Hz (matches mock)
// Drop emitters silent for > 10 minutes. v1's surveillance_sensors topics
// publish at 30-90 s per emitter but go quiet between bursts — a tighter
// window made the folded service counts bounce on every emit cycle as
// snapshots fell out and rejoined. 10 min is generous enough that any
// actively-publishing emitter stays in the map.
const SURVEILLANCE_STALE_MS = 10 * 60_000;
/** Cold-start window after MQTT connect during which surveillance
 *  services that haven't published yet show `Connecting` instead of
 *  `Offline`. 90 s covers v1's worst-case 30-90 s per-emitter publish
 *  cadence — past this, the operator should reasonably conclude the
 *  emitter is silent. */
const SURVEILLANCE_CONNECTING_MS = 90_000;
/** A service that publishes targets but never a sensors topic (Ottawa) is only
 *  discoverable from its traffic topic. The traffic wildcard carries the
 *  high-rate non-service topics, so it is held open for a few seconds only —
 *  once on connect, then periodically to catch services that appear later. */
const TRAFFIC_PROBE_MS = 4_000;
const TRAFFIC_PROBE_EVERY_MS = 5 * 60_000;
/** Traffic seen within this window marks a sensors-less service Active. */
const TRAFFIC_LIVE_MS = 60_000;
const TARGET_STALE_MS = 15_000; // drop fused targets we haven't seen for 15 s
/** MAVLink subtypes (ATTITUDE, GPS_RAW_INT, etc.) arrive at 5–20 Hz each.
 *  Coalesce into one emit per drone per tick. 250 ms = 4 Hz, matches the
 *  canonical telemetry cadence consumers expect. */
const MAVLINK_FLUSH_MS = 250;
/** The raw GCS stream's claim on a field lapses after this much silence (ms). */
const GCS_STALE_MS = 5_000;

/** Minimal c2 envelope used when only TIS-B status is known. Every other
 *  field is null/empty until a richer source ships. */
function emptyC2(droneId: string): C2LinksEnvelope {
  return {
    drone_id: droneId,
    links: [],
    continuity_pct: null,
    aa_agent_status: null,
    aa_latency_ms: null,
    aa_agent_id: null,
    aa_acceptance_mode: null,
    aa_platform: null,
    aa_msg_rate: null,
    aa_last_msg_at: null,
    aa_producer_id: null,
    aa_tenant: null,
    gcs_agent_status: null,
    gcs_latency_ms: null,
    gcs_platform: null,
    gcs_msg_rate: null,
    gcs_last_msg_at: null,
    gcs_producer_id: null,
    gcs_tenant: null,
    gcs_platform_name: null,
    gcs_version: null,
    autopilot: null,
    flight_controller: null,
    deployment: null,
    sticks_type: null,
    dock_id: null,
    video_streams: [],
    ec: {
      adsbOut: { status: 'N/A', freq: null, callsign: null },
      adsbIn: { status: 'N/A', freq: null },
      tisb: { status: 'N/A', towers: null },
    },
    received_at: new Date().toISOString(),
  };
}

export class MqttSourceLegacy implements MqttSource {
  private client: MqttClient | null = null;
  private state: ConnectionState = 'disconnected';
  private listeners = new Set<MqttListener>();
  private stateListeners = new Set<(s: ConnectionState) => void>();
  private rawListeners = new Set<RawMqttListener>();
  private rawSeq = 0;
  private wildcardOn = false;

  /** Last telemetry envelope per drone — needed for partial-update
   *  backfill from `traffic_control`. The source is the only place
   *  that knows how to merge partials into the full envelope. */
  private lastTelemetry = new Map<string, TelemetryEnvelope>();
  /** Last c2 envelope per drone — same reasoning for TISB backfill. */
  private lastC2 = new Map<string, C2LinksEnvelope>();
  /** Drones whose cached c2 envelope has been replayed after the drone was
   *  first published. See `replayC2` — guards a lost-update race. */
  private c2Replayed = new Set<string>();
  /** Last OI envelope per drone — built from `flightplan/<UAS_ID>`. Kept
   *  here so future partial-update sources (live conformance, mission
   *  cursor, etc.) can merge into a coherent snapshot before emit. */
  private lastOi = new Map<string, OiLiveEnvelope>();

  /** Per-emitter target fusion table. Key = aircraft key (flight or hex
   *  or position fallback). Value carries:
   *   - `slots`: map of emitter → its most recent partial + seenAt.
   *     Per-emitter timestamps let us age each source out independently.
   *   - `winner`: the emitter id we're currently quoting (position, alt,
   *     speed, type, trust come from this slot). Sticky — we only repick
   *     when the winner ages out OR a strictly higher-trust emitter shows
   *     up. Stops the per-tick flicker when multiple high-trust emitters
   *     (live_ads, live_mlat, live_vision) all report the same target
   *     with slightly different values (different category, gs unit,
   *     altitude source, lat/lng precision).
   *  When every emitter ages out, the whole target is dropped. */
  private fusedTargets = new Map<
    string,
    {
      slots: Map<string, { partial: PartialTarget; seenAt: number }>;
      winner: string | null;
    }
  >();
  private targetsTimer: number | null = null;

  /** Per-v1-emitter sensor snapshots. The unified emit folds these into
   *  the 5 canonical v2 services every SURVEILLANCE_EMIT_MS. Entries
   *  older than SURVEILLANCE_STALE_MS are evicted (emitter went silent). */
  private sensorSnapshots = new Map<string, { snapshot: EmitterSnapshot; seenAt: number }>();
  /** Every surveillance service seen this session. Snapshots are evicted after
   *  SURVEILLANCE_STALE_MS, but a service that has gone quiet should stay listed
   *  (as Offline) rather than vanish — this is what keeps it. */
  private discoveredServices = new Set<string>();
  /** `surveillance_traffic/<id>` topics subscribed on discovery (the curated
   *  services are subscribed up front). Per client: a new client starts empty,
   *  and the next sensors snapshot re-announces each service. */
  private dynamicTrafficSubs = new Set<string>();
  /** Last traffic message per service — lets a service with no sensors topic
   *  read Active while its targets are flowing. */
  private trafficSeenAt = new Map<string, number>();
  private probing = false;
  private probeTimer: number | null = null;
  private probeEndTimer: number | null = null;
  private surveillanceTimer: number | null = null;
  /** Wall-clock timestamp of the most recent MQTT `connect` event.
   *  Used as the anchor for the surveillance Connecting → Offline
   *  transition (services that haven't published within
   *  SURVEILLANCE_CONNECTING_MS of this point flip to Offline). Set on
   *  every `connect` so reconnects re-extend the grace window — the
   *  alternative ("anchor to the first ever connect") would silently
   *  mark a service Offline on a network blip even though we genuinely
   *  haven't heard from it post-reconnect. */
  private mqttConnectedAtMs: number | null = null;
  /** Content signature of the last surveillance envelope we emitted.
   *  The timer fires every 5 s but most cycles have no actual change
   *  (sensor snapshots refresh sparingly). Skipping unchanged emits
   *  avoids hammering useSurvStore on every interval, which (because
   *  apply() always builds a new byId object) re-syncs the map layer
   *  and re-renders every dependent component for no visible reason. */
  private lastSurveillanceSig: string | null = null;

  /** Drones with pending MAVLink-derived field updates. Flushed at most
   *  once per MAVLINK_FLUSH_MS so we don't hammer the store with one
   *  emit per subtype message. */
  private mavlinkDirty = new Set<string>();
  private mavlinkFlushTimer: number | null = null;

  /** Sliding-window message timestamps per drone × role (aa | gcs).
   *  Used to compute aa_msg_rate / gcs_msg_rate for the Agents-tab cards
   *  in live mode. Keeps the last 5 s of arrival times. */
  private agentMsgWindow = new Map<string, number[]>();
  /** Throttle c2 emits triggered by agent-producer updates. Multiple
   *  MAVLink subtype messages arrive per second per drone; we don't
   *  want to refan the c2 envelope at 20+ Hz. */
  private lastAgentEmitMs = new Map<string, number>();

  /** Weather station fusion table, keyed by station id. v1 publishes
   *  per-source *batches* under `weather/sensors` (one msg carries the
   *  full MSC list, the next the full Alberta511 list, the next the
   *  Netatmo list — confirmed by sniff). Replacing wholesale would
   *  clobber whichever sources weren't in the most recent message.
   *  Merging by id preserves cross-source coverage; the most recent
   *  reading per station wins. */
  private weatherStationsById = new Map<string, WxSensorRecord>();

  /** Per-drone set of TelemetryEnvelope keys that MAVLink has ever
   *  supplied a non-null value for. Once a field is "claimed" by MAVLink,
   *  later networkid messages stop touching it — networkid is 1 Hz and
   *  MAVLink is 4 Hz, so without this guard they'd race and the UI would
   *  flicker between two slightly different values every second
   *  (climbRate / heading / groundspeed / etc.). Drones with no MAVLink
   *  feed (lean publishers like SIMTL69/SIMTL71) keep all fields driven
   *  by networkid because their set stays empty. */
  private mavlinkClaimed = new Map<string, Set<string>>();

  /** The aircraft whose raw GCS stream (`v1/input/aircraft/{sn}/gcs_telemetry`)
   *  is subscribed. One at a time — the stream is ~91 kB/s per aircraft, so it
   *  follows the selected drone instead of covering the fleet (ENG-271). */
  private focusedAircraft: string | null = null;

  /** Fields the raw GCS stream has claimed, per aircraft. Kept apart from
   *  `mavlinkClaimed`: the legacy MAVLink path claims some of the same keys
   *  (gps_*), and releasing the stream's claim must not drop that one. */
  private gcsClaimed = new Map<string, Set<string>>();
  /** Wall-clock of the last stream message for the focused aircraft. */
  private gcsLastMsgMs = 0;
  /** When each message type was last parsed (the stream is ~105 msg/s, so we thin it). */
  private gcsTypeLastMs = new Map<string, number>();

  async connect(): Promise<void> {
    // Allow only 'disconnected' through. 'reconnecting' = mqtt.js is already
    // re-establishing under the hood; spawning a second client would orphan
    // the first. 'connecting' / 'connected' are obviously no-ops.
    if (this.state !== 'disconnected') return;

    const url = config.mqtt.url;
    if (!url) {
      log.error('MqttSourceLegacy: VITE_MQTT_URL is empty — cannot connect');
      this.setState('disconnected');
      return;
    }

    log.info('MqttSourceLegacy: connecting', { url, username: config.mqtt.username || '(none)' });
    this.setState('connecting');

    // Watchdog: if neither 'connect' nor 'error' has resolved the attempt within
    // the connect timeout (+ grace), the transport is hung (e.g. wrong host/port,
    // blocked WSS) and mqtt.js may keep retrying without a clear signal. Emit one
    // explicit warning so a stuck connection is visible rather than silent.
    if (this.connectWatchdog != null) clearTimeout(this.connectWatchdog);
    this.connectWatchdog = window.setTimeout(() => {
      if (this.state !== 'connected') {
        log.warn('MqttSourceLegacy: still not connected', {
          url,
          state: this.state,
          afterMs: CONNECT_TIMEOUT_MS + 5_000,
        });
      }
    }, CONNECT_TIMEOUT_MS + 5_000);

    const client = mqtt.connect(url, {
      username: config.mqtt.username || undefined,
      password: config.mqtt.password || undefined,
      keepalive: KEEPALIVE_S,
      reconnectPeriod: RECONNECT_MS,
      connectTimeout: CONNECT_TIMEOUT_MS,
      rejectUnauthorized: false,
    });

    client.on('connect', (packet) => {
      log.info('MqttSourceLegacy: connected', {
        url,
        reasonCode: (packet as { reasonCode?: number } | undefined)?.reasonCode ?? 0,
      });
      this.reconnectAttempts = 0;
      if (this.connectWatchdog != null) {
        clearTimeout(this.connectWatchdog);
        this.connectWatchdog = null;
      }
      this.setState('connected');
      // Anchor for surveillance Connecting → Offline transition. Reset
      // on each connect (including post-reconnect) so the grace window
      // restarts for any service still silent at that point.
      this.mqttConnectedAtMs = Date.now();
      // Topic family list (LEGACY_SUBSCRIPTIONS) mirrors the discovery sniff
      // and is the single source of truth shared with the Data Stream tree.
      // Each subscription is independent so a failure on one doesn't block the
      // others. Per-topic rationale lives in lib/mqtt/subscriptions.ts.
      for (const filter of LEGACY_SUBSCRIPTIONS) {
        this.subscribeTopic(client, filter);
      }
      // Re-establish the selected aircraft's raw stream after a (re)connect.
      if (this.focusedAircraft && this.gcsStreamAvailable()) {
        this.subscribeTopic(client, gcsStreamTopic(this.focusedAircraft));
      }

      // Canonical v1 Events & Alerts are part of LEGACY_SUBSCRIPTIONS above
      // (v1Topics.droneEventsWild / aircraftAlertsWild / aircraftAlertsSevWild /
      // broadcastAlerts). routeMessage() maps them via parseDocEvent /
      // parseDocAlert → emit({ kind: 'events' }) → useAlertsStore, which feeds
      // the Events tab (per drone_id), the Live Alerts pane, and the
      // DaaConflictModal. Alerts remain the single source of truth from MQTT —
      // no client-side alert derivation. When the backend starts publishing on
      // these topics, they surface automatically; until then this is silent.

      // Unified surveillance envelope emit timer (drains per-emitter
      // snapshots into the v2 services list at SURVEILLANCE_EMIT_MS).
      if (this.surveillanceTimer == null) {
        this.surveillanceTimer = window.setInterval(
          () => this.emitSurveillance(),
          SURVEILLANCE_EMIT_MS,
        );
      }

      this.startTrafficProbes(client);

      // Start the targets emission interval here (not in the constructor)
      // so HMR teardown via disconnect() actually clears it.
      if (this.targetsTimer == null) {
        this.targetsTimer = window.setInterval(() => this.emitFusedTargets(), TARGETS_EMIT_MS);
      }
    });

    client.on('reconnect', () => {
      this.reconnectAttempts += 1;
      log.warn('MqttSourceLegacy: reconnecting', { url, attempt: this.reconnectAttempts });
      this.setState('reconnecting');
    });
    client.on('offline', () => {
      log.warn('MqttSourceLegacy: offline — no broker connection', { url });
      this.setState('disconnected');
    });
    client.on('close', () => {
      log.info('MqttSourceLegacy: connection closed', { url });
      this.setState('disconnected');
    });
    client.on('error', (err) => {
      // mqtt.js surfaces broker CONNACK rejections here. `code` carries the
      // reason code (134 = bad username/password, 135 = not authorized, …) —
      // log it explicitly so credential / ACL failures are no longer silent.
      // Log the username for triage but NEVER the password.
      const code = (err as { code?: number | string } | undefined)?.code;
      log.error('MqttSourceLegacy: client error', {
        url,
        username: config.mqtt.username || '(none)',
        code: code ?? '(none)',
        reason: reasonCodeText(code),
        message: err?.message ?? String(err),
      });
    });

    client.on('message', (topic, payload, packet) => {
      if (this.consumeProbeMessage(topic)) return;
      this.routeMessage(topic, payload);
      this.emitRaw(topic, payload, packet);
    });

    this.dynamicTrafficSubs.clear();
    this.client = client;
  }

  async disconnect(): Promise<void> {
    this.stopTrafficProbes();
    if (this.targetsTimer != null) {
      clearInterval(this.targetsTimer);
      this.targetsTimer = null;
    }
    if (this.mavlinkFlushTimer != null) {
      clearTimeout(this.mavlinkFlushTimer);
      this.mavlinkFlushTimer = null;
    }
    if (this.surveillanceTimer != null) {
      clearInterval(this.surveillanceTimer);
      this.surveillanceTimer = null;
    }
    this.mavlinkDirty.clear();
    if (this.connectWatchdog != null) {
      clearTimeout(this.connectWatchdog);
      this.connectWatchdog = null;
    }
    if (!this.client) {
      this.setState('disconnected');
      return;
    }
    try {
      this.client.removeAllListeners();
      this.client.end(true);
    } catch (err) {
      log.warn('MqttSourceLegacy: disconnect error (ignored)', { error: String(err) });
    }
    this.client = null;
    // NB: deliberately don't clear lastTelemetry / lastC2 / fusedTargets.
    // If the same singleton is later reconnected (e.g. a future "Reconnect"
    // affordance, or programmatic reconnect after network glitch), the
    // backfilled per-drone snapshots are still useful — losing them caused
    // visible UI flicker on every reconnect. Live-mode page reloads create
    // a fresh singleton anyway, so persistence here is harmless.
    this.setState('disconnected');
  }

  subscribe(listener: MqttListener): () => void {
    this.listeners.add(listener);
    // Replay the current snapshot to the freshly-attached listener so the
    // store doesn't have to wait until the next 5-s / 1-s emit cycle to
    // see any data. Without this, the surveillance map layer can subscribe
    // mid-cycle, find byId empty, and not render pins until the next
    // unified emit fires.
    queueMicrotask(() => {
      if (!this.listeners.has(listener)) return; // unsubscribed between
      try {
        // Per-drone envelopes (telemetry / c2 / oi)
        for (const env of this.lastTelemetry.values())
          listener({ kind: 'telemetry', payload: env });
        for (const env of this.lastC2.values()) listener({ kind: 'c2links', payload: env });
        for (const env of this.lastOi.values()) listener({ kind: 'oi', payload: env });
        // Global envelopes
        // Replay surveillance even with zero snapshots so the listener
        // immediately sees the canonical 7-service list (in Connecting
        // / Offline states) rather than waiting for the next 5 s timer.
        {
          const snapshots = [...this.sensorSnapshots.values()].map((e) => e.snapshot);
          const connectingUntilMs =
            this.mqttConnectedAtMs != null
              ? this.mqttConnectedAtMs + SURVEILLANCE_CONNECTING_MS
              : 0;
          listener({
            kind: 'surveillance',
            payload: snapshotsToEnvelope(
              snapshots,
              connectingUntilMs,
              this.discoveredServices,
              this.trafficLive(),
              this.visibleByService(),
            ),
          });
        }
        if (this.fusedTargets.size > 0) {
          // Lazy: reuse the timer-driven emit path so target shape stays in lockstep.
          this.emitFusedTargets();
        }
        if (this.weatherStationsById.size > 0) {
          listener({
            kind: 'weather',
            payload: {
              stations: [...this.weatherStationsById.values()],
              received_at: new Date().toISOString(),
            },
          });
        }
      } catch (err) {
        log.warn('MqttSourceLegacy: replay on subscribe failed', { error: String(err) });
      }
    });
    return () => this.listeners.delete(listener);
  }

  getState(): ConnectionState {
    return this.state;
  }

  onStateChange(listener: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  /* ---------------- private helpers ---------------- */

  /** While the wildcard probe is open, a traffic message is for discovery only.
   *  A service we already subscribe to is delivered by its own subscription and
   *  falls through; an unseen one is noted and subscribed; the rest (stream,
   *  ads_count, …) are dropped unparsed. Returns true when the message is spent. */
  private consumeProbeMessage(topic: string): boolean {
    if (!this.probing || !topic.startsWith('surveillance_traffic/')) return false;
    const id = topic.slice('surveillance_traffic/'.length);
    if (this.isSubscribedService(id)) return false;
    if (isServiceEmitter(id)) this.noteService(id);
    return true;
  }

  private isSubscribedService(id: string) {
    return (KNOWN_EMITTERS as readonly string[]).includes(id) || this.dynamicTrafficSubs.has(id);
  }

  private startTrafficProbes(client: MqttClient) {
    this.stopTrafficProbes();
    const probe = () => {
      if (this.client !== client || !client.connected) return;
      this.probing = true;
      client.subscribe('surveillance_traffic/+', { qos: 0 });
      this.probeEndTimer = window.setTimeout(() => {
        this.probing = false;
        client.unsubscribe('surveillance_traffic/+');
      }, TRAFFIC_PROBE_MS);
    };
    probe();
    this.probeTimer = window.setInterval(probe, TRAFFIC_PROBE_EVERY_MS);
  }

  private stopTrafficProbes() {
    this.probing = false;
    if (this.probeTimer != null) window.clearInterval(this.probeTimer);
    if (this.probeEndTimer != null) window.clearTimeout(this.probeEndTimer);
    this.probeTimer = null;
    this.probeEndTimer = null;
  }

  /** Target ids each service currently sees, from the live fusion slots (the
   *  slots are pruned at TARGET_STALE_MS, so a silent service empties out). */
  private visibleByService(): Map<string, string[]> {
    const out = new Map<string, string[]>();
    for (const [targetId, entry] of this.fusedTargets) {
      for (const emitter of entry.slots.keys()) {
        const ids = out.get(emitter);
        if (ids) ids.push(targetId);
        else out.set(emitter, [targetId]);
      }
    }
    return out;
  }

  /** Services whose targets arrived recently. */
  private trafficLive(): Set<string> {
    const cutoff = Date.now() - TRAFFIC_LIVE_MS;
    const live = new Set<string>();
    for (const [id, at] of this.trafficSeenAt) if (at >= cutoff) live.add(id);
    return live;
  }

  private subscribeTopic(client: MqttClient, topic: string) {
    client.subscribe(topic, { qos: 0 }, (err) => {
      if (err) log.error(`MqttSourceLegacy: subscribe '${topic}' failed`, { error: String(err) });
      else log.info(`MqttSourceLegacy: subscribed '${topic}'`);
    });
  }

  /** Wall-clock at which we last transitioned out of 'connected'. Used
   *  to "rewind" the per-emitter staleness clock when we reconnect —
   *  otherwise a 10 s broker keepalive timeout consumes 10 s of every
   *  emitter slot's 15 s grace, causing aircraft to mass-evict on the
   *  first emit cycle after reconnect. */
  private disconnectedSinceMs: number | null = null;

  /** Count of mqtt.js 'reconnect' events since the last successful connect,
   *  reset to 0 on connect. Logged so a reconnect loop is visible. */
  private reconnectAttempts = 0;

  /** Timer that fires one "still not connected" warning if an attempt neither
   *  connects nor errors within the connect timeout. Cleared on connect/disconnect. */
  private connectWatchdog: number | null = null;

  private setState(s: ConnectionState) {
    if (this.state === s) return;
    const prev = this.state;
    log.debug('MqttSourceLegacy: state', { from: prev, to: s });
    if (prev === 'connected' && s !== 'connected') {
      this.disconnectedSinceMs = Date.now();
    } else if (s === 'connected' && this.disconnectedSinceMs != null) {
      const offlineMs = Date.now() - this.disconnectedSinceMs;
      this.disconnectedSinceMs = null;
      if (offlineMs > 0) this.bumpStalenessClocks(offlineMs);
    }
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }

  /** Add `offlineMs` to every per-emitter slot's `seenAt`, effectively
   *  freezing the staleness clock for the period we were disconnected.
   *  Slot freshness should reflect time the broker had a chance to
   *  republish — not wall-clock time including downtime. */
  private bumpStalenessClocks(offlineMs: number) {
    for (const entry of this.fusedTargets.values()) {
      for (const slot of entry.slots.values()) {
        slot.seenAt += offlineMs;
      }
    }
    for (const entry of this.sensorSnapshots.values()) {
      entry.seenAt += offlineMs;
    }
  }

  private emit(msg: MqttPayload) {
    for (const l of this.listeners) l(msg);
  }

  private emitRaw(topic: string, payload: Buffer, packet?: { qos?: number; retain?: boolean }) {
    if (this.rawListeners.size === 0) return; // zero cost when DS isn't open
    const raw = payload.toString('utf8');
    let parsed: unknown = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      /* keep string */
    }
    const msg: RawMqttMessage = {
      id: `${++this.rawSeq}:${topic}`,
      topic,
      payload: parsed,
      raw,
      qos: (packet?.qos ?? 0) as 0 | 1 | 2,
      retain: packet?.retain ?? false,
      bytes: payload.byteLength,
      receivedAt: Date.now(),
    };
    for (const l of this.rawListeners) l(msg);
  }

  subscribeRaw(listener: RawMqttListener): () => void {
    this.rawListeners.add(listener);
    return () => this.rawListeners.delete(listener);
  }

  canPublish(): boolean {
    return config.features.mqttPublish && this.state === 'connected' && this.client != null;
  }

  async publish(topic: string, payload: string, opts?: MqttPublishOptions): Promise<void> {
    if (!this.canPublish() || !this.client) {
      throw new Error('Publishing is disabled or the broker is not connected');
    }
    await new Promise<void>((resolve, reject) => {
      this.client!.publish(
        topic,
        payload,
        { qos: opts?.qos ?? 0, retain: opts?.retain ?? false },
        (err) => (err ? reject(err) : resolve()),
      );
    });
  }

  /**
   * Data Stream "subscribe all" toggle.
   *
   * Not a bare `#` any more: the sandbox broker's ACL grants `v1/shared/#`
   * and `v1/output/#` only and returns SUBACK 128 for `#`. And the obvious
   * replacement — `v1/output/#` — would pull in the per-field alias branch
   * (`.../{sn}/drones/{fieldName}`), 97 msg/s per aircraft of duplicate
   * data. `WILDCARD_SUBSCRIPTIONS` uses `v1/output/aircraft/+/+`, which
   * cannot match that deeper path. See subscriptions.ts.
   */
  setWildcard(on: boolean): void {
    if (on === this.wildcardOn || !this.client) return;
    this.wildcardOn = on;
    for (const filter of WILDCARD_SUBSCRIPTIONS) {
      if (on) this.subscribeTopic(this.client, filter);
      else this.client.unsubscribe(filter);
    }
  }

  /** Dispatch incoming v1 messages by topic prefix. Each handler is
   *  defensive — malformed payloads are silently dropped so one bad
   *  message can't poison the rest of the stream. */
  private routeMessage(topic: string, payload: Buffer) {
    // `v1/output/…` — category-first namespace (ticket 86e2jbe3d).
    // Checked first: it is the live schema on cut-over tenants, and the
    // per-field alias gate below must run before any broader match.
    if (topic.startsWith('v1/output/')) {
      // Defence in depth. The subscription list never asks for the alias
      // branch, but a wildcard slip elsewhere (or the "subscribe all"
      // toggle on a future broker config) would otherwise push ~97 msg/s
      // per aircraft of duplicate fields through the store.
      if (isPerFieldAliasTopic(topic)) return;
      const parsed = parseOutputTopic(topic);
      if (!parsed) {
        // Tenant-wide topics: v1/output/events, v1/output/alerts/{sev}.
        if (topic === 'v1/output/events') this.handleOutputEvent(payload);
        else if (topic.startsWith('v1/output/alerts')) this.handleOutputAlert(payload);
        return;
      }
      switch (parsed.kind) {
        case 'networkid':
          this.handleOutputNetworkid(payload);
          return;
        case 'telemetry':
          this.handleOutputTelemetry(payload);
          return;
        case 'oi/declaration':
          this.handleFlightplan(payload);
          return;
        case 'waypoint_pred':
        case 'waypoint_approached':
          this.handleOutputWaypoint(payload);
          return;
        default:
          // Notably NOT handled: 'events'. `v1/output/aircraft/{sn}/events`
          // was a developer mistake in the NID/NID-DD jobs, corrected to
          // the doc-specified tenant-wide `v1/output/events` (handled
          // above, before parseOutputTopic runs). Not subscribed, so this
          // branch should never actually see 'events' — kept as a no-op
          // rather than removed so a stray message doesn't crash routing.
          return;
      }
    }
    if (isGcsStreamTopic(topic)) {
      this.handleGcsStream(payload);
      return;
    }
    if (isLiveRfTopic(topic)) {
      this.handleLiveRf(liveRfKind(topic), payload);
      return;
    }
    if (topic.startsWith('networkid/')) {
      this.handleNetworkid(payload);
      return;
    }
    if (topic === 'traffic_control') {
      this.handleTrafficControl(payload);
      return;
    }
    if (topic.startsWith('TISB Stream/')) {
      this.handleTisbStream(payload);
      return;
    }
    if (topic.startsWith('surveillance_traffic/')) {
      const emitter = topic.slice('surveillance_traffic/'.length);
      this.handleSurveillanceTraffic(emitter, payload);
      return;
    }
    if (topic.startsWith('flysafe/v1/')) {
      // Two parallel jobs per message: demux the MAVLink payload into
      // telemetry, AND stamp the producer/tenant attribution onto c2 so
      // the agent panels can show which source is live (ticket 86e1hnwf5
      // item 1). Topic carries the producer slug; payload doesn't.
      this.handleAgentProducer(topic);
      this.handleMavlink(payload);
      return;
    }
    if (topic.startsWith('flightplan/')) {
      this.handleFlightplan(payload);
      return;
    }
    if (topic.startsWith('surveillance_sensors/')) {
      const emitter = topic.slice('surveillance_sensors/'.length);
      this.handleSurveillanceSensors(emitter, payload);
      return;
    }
    // Canonical v1 Events & Alerts (FLYsafe MQTT Topic doc) — distinct streams:
    //  • events → EventEnvelope → kind 'events' → useAlertsStore → Events tab
    //  • alerts → AlertEnvelope → kind 'alert'  → useAlertFeedStore → Live Alerts pane
    if (isV1EventTopic(topic)) {
      this.handleV1Event(payload);
      return;
    }
    if (isV1AlertTopic(topic)) {
      this.handleV1Alert(payload);
      return;
    }
    if (topic === 'weather/sensors') {
      this.handleWeatherSensors(payload);
      return;
    }
  }

  /** `v1/{tenant}/aircraft/{serial}/drones/events` → EventEnvelope. */
  private handleV1Event(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = parseDocEvent(raw);
    if (env) this.emit({ kind: 'events', payload: env });
  }

  /** `v1/{tenant}/aircraft/{serial}/alerts[/severity]` + broadcast → EventEnvelope. */
  private handleV1Alert(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = parseDocAlert(raw);
    if (env) this.emit({ kind: 'alert', payload: env });
  }

  /** `v1/output/events` (tenant-wide) and `v1/output/aircraft/{sn}/events`
   *  (per-aircraft, NID jobs) — ticket 86e2jbaqr Parts 3–7. Separate from
   *  `handleV1Event` because the payload shape genuinely differs
   *  (`droneIds[]` vs `droneId`, `sourceTenant` vs `tenantId`) — see
   *  output/events.ts. `handleV1Event` stays as-is for airmarket/sait,
   *  which haven't cut over. */
  private handleOutputEvent(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = outputEventToEnvelope(raw);
    if (env) this.emit({ kind: 'events', payload: env });
  }

  /** `v1/output/alerts/{severity}` — ticket Parts 5, 7. */
  private handleOutputAlert(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = outputAlertToEnvelope(raw);
    if (env) this.emit({ kind: 'alert', payload: env });
  }

  /** `v1/output/aircraft/{sn}/waypoint_pred` and `.../waypoint_approached` —
   *  ENG-173. Same payload shape the alerts key carries, so it reuses
   *  `outputAlertToEnvelope`. Emitted as its own kind (not `alert`) so it feeds
   *  only the waypoint store: the Live Alerts pane already gets the alerts-key
   *  copy of `waypoint_approached`, and must never get `waypoint_pred`. */
  private handleOutputWaypoint(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = outputAlertToEnvelope(raw);
    if (env) this.emit({ kind: 'waypoint', payload: env });
  }

  private handleWeatherSensors(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = weatherSensorsToEnvelope(raw);
    if (!env || env.stations.length === 0) return;
    // Merge in, tracking whether anything material actually changed.
    // The broker re-publishes the same batch every ~25 s — without a
    // dirty check we'd refan identical updates into the store.
    let changed = false;
    for (const s of env.stations) {
      const prev = this.weatherStationsById.get(s.id);
      if (
        !prev ||
        prev.observed_at !== s.observed_at ||
        prev.temp_c !== s.temp_c ||
        prev.wind_speed_kmh !== s.wind_speed_kmh ||
        prev.wind_dir_deg !== s.wind_dir_deg ||
        prev.humidity_pct !== s.humidity_pct ||
        prev.pressure_hpa !== s.pressure_hpa
      ) {
        changed = true;
      }
      this.weatherStationsById.set(s.id, s);
    }
    if (!changed) return;
    this.emit({
      kind: 'weather',
      payload: {
        stations: [...this.weatherStationsById.values()],
        received_at: env.received_at,
      },
    });
  }

  /** Preserve backfilled fields that don't come from networkid itself —
   *  notably `status` / `status_label` (sourced from `traffic_control`)
   *  and any MAVLink-derived field that the demuxer has populated. The
   *  v1 networkid payload returns null for these, so a naive overwrite
   *  would erase the backfill once per second and the UI would flicker
   *  between "Active" and "—" on every networkid tick.
   *
   *  Rule: prefer the new networkid value when it's non-null; otherwise
   *  fall back to whatever was already in the snapshot. Identity and
   *  position fields are always taken from networkid (it's the
   *  authoritative source for those). */
  private handleNetworkid(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = networkidToTelemetry(raw);
    if (!env) return;
    const prev = this.lastTelemetry.get(env.drone_id);
    // If MAVLink has claimed any fields for this drone, mask them in the
    // envelope we're about to merge — mergeNonNull treats null as "no
    // change", so MAVLink's value stays put. Without this, networkid's
    // 1 Hz updates would clobber MAVLink's 4 Hz values for shared fields
    // (climbRate / heading / groundspeed / aglM / etc.) and the UI would
    // flicker between two slightly different readings every second.
    const claimed = this.claimedFor(env.drone_id);
    let toMerge: TelemetryEnvelope = env;
    if (claimed && claimed.size > 0) {
      const masked: Record<string, unknown> = { ...env };
      for (const f of claimed) masked[f] = null;
      toMerge = masked as TelemetryEnvelope;
    }
    const merged: TelemetryEnvelope = prev ? mergeNonNull(prev, toMerge) : toMerge;
    this.lastTelemetry.set(env.drone_id, merged);
    this.emit({ kind: 'telemetry', payload: merged });

    // Rich-shape backfill into the OI envelope (ticket 86e1hnwf5 audit).
    // Aircraft-agent publishers ship pilot / mission / legal-profile
    // metadata in every networkid message; the flightplan topic doesn't
    // carry these fields. Merge them in so the OI panel reads as
    // populated rather than "—".
    this.mergeNetworkidIntoOi(raw, env.drone_id);
  }

  /** Merge mission metadata from a rich networkid payload into the
   *  per-drone OI snapshot. No-op when the payload is lean (no rich
   *  fields), or when none of the rich values have actually changed
   *  since the last emit — keeps the OI envelope from refanning at
   *  networkid's 1 Hz cadence unnecessarily.
   *
   *  Networkid only carries descriptive metadata (pilot / mission name /
   *  legal profile / flight_guid). The OI envelope itself — polygon,
   *  schedule, status — is owned by `flightplan/<UAS_ID>`. So this
   *  function strictly *decorates* an envelope that already exists; it
   *  must NOT fabricate one. Otherwise networkid would manufacture a
   *  ghost OI (status defaulted, polygon null) for any drone whose
   *  flightplan message never arrives, and downstream consumers
   *  (OI list, map polygon layer, drone detail tabs) would render
   *  that fabricated record as if it were a real declared OI. */
  private mergeNetworkidIntoOi(raw: unknown, droneId: string) {
    const update = networkidToOiUpdate(raw);
    if (!update) return;
    const prev = this.lastOi.get(droneId);
    // No prior flightplan envelope → there is no OI to decorate. Drop
    // the metadata until flightplan arrives and seeds the envelope.
    if (!prev) return;
    // Track whether anything visible would actually change. If every
    // rich field already matches the previous snapshot, skip the emit.
    const wouldChange = (key: keyof OiLiveEnvelope, next: string | null | undefined) =>
      next != null && (prev[key] ?? null) !== next;
    const changed =
      wouldChange('pilot_name', update.partial.pilot_name) ||
      wouldChange('op_type', update.partial.op_type) ||
      wouldChange('legal_rule', update.partial.legal_rule) ||
      wouldChange('mission_name', update.partial.mission_name) ||
      wouldChange('mission_notes', update.partial.mission_notes) ||
      wouldChange('mission_location', update.partial.mission_location) ||
      (update.partial.oi_ref_id != null && prev.oi_ref_id !== update.partial.oi_ref_id);
    if (!changed) return;

    const merged: OiLiveEnvelope = {
      ...prev,
      oi_ref_id: update.partial.oi_ref_id ?? prev.oi_ref_id,
      op_type: update.partial.op_type ?? prev.op_type,
      legal_rule: update.partial.legal_rule ?? prev.legal_rule,
      pilot_name: update.partial.pilot_name ?? prev.pilot_name,
      mission_name: update.partial.mission_name ?? prev.mission_name,
      mission_notes: update.partial.mission_notes ?? prev.mission_notes,
      mission_location: update.partial.mission_location ?? prev.mission_location,
      received_at: new Date().toISOString(),
    };
    this.lastOi.set(droneId, merged);
    this.emit({ kind: 'oi', payload: merged });
  }

  /* ---------------- `v1/output` namespace (86e2jbe3d) ---------------- */

  /** `v1/output/aircraft/{sn}/networkid` — ASTM F3411 Remote ID.
   *
   *  The baseline envelope for a drone: this is the only topic in the new
   *  namespace carrying identity (callsign, uaType → model/radii), so it
   *  is the one allowed to create a drone. Mirrors `handleNetworkid`'s
   *  MAVLink-masking discipline — MAVLink runs at 4 Hz against this
   *  stream's 1.7 Hz, and without the mask the two race and the UI
   *  flickers between two readings every tick. */
  private handleOutputNetworkid(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const env = remoteIdToTelemetry(raw);
    if (!env) return;

    const prev = this.lastTelemetry.get(env.drone_id);
    const claimed = this.claimedFor(env.drone_id);
    let toMerge: TelemetryEnvelope = env;
    if (claimed && claimed.size > 0) {
      const masked: Record<string, unknown> = { ...env };
      for (const f of claimed) masked[f] = null;
      toMerge = masked as TelemetryEnvelope;
    }
    const merged: TelemetryEnvelope = prev ? mergeNonNull(prev, toMerge) : toMerge;
    this.lastTelemetry.set(env.drone_id, merged);
    this.emit({ kind: 'telemetry', payload: merged });
    this.replayC2(env.drone_id);

    // Mission metadata from the `ext` block decorates an OI envelope that
    // `oi/declaration` seeds. Never creates one — otherwise a drone with
    // no declared volume renders a ghost OI.
    const oi = remoteIdToOiUpdate(raw);
    if (oi) this.applyOiPartial(oi.drone_id, oi.partial);
  }

  /** `v1/output/aircraft/{sn}/telemetry` — airframe state.
   *
   *  Carries no identity fields, so it never creates a drone: a telemetry
   *  message arriving before the first networkid message is dropped
   *  rather than fabricating an aircraft with no callsign or radii. At
   *  1.7 Hz the wait is under a second. */
  private handleOutputTelemetry(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    // Peek the serial so we can hand the parser the airframe we already
    // know from networkid's `uaType` — ArduPilot's `custom_mode` decodes
    // differently for plane vs copter and this topic carries no frame.
    const serial =
      typeof raw === 'object' && raw != null
        ? ((raw as Record<string, unknown>).aircraft_serial ?? (raw as Record<string, unknown>).sn)
        : null;
    const knownType =
      typeof serial === 'string' ? (this.lastTelemetry.get(serial)?.drone_type ?? null) : null;

    const update = outputTelemetryToPartial(raw, knownType);
    if (!update) return;

    const prev = this.lastTelemetry.get(update.droneId);
    if (prev) {
      // Drop any field MAVLink has claimed — mergeNonNull would otherwise
      // let this topic overwrite the higher-rate source.
      let partial = update.partial;
      const claimed = this.claimedFor(update.droneId);
      if (claimed && claimed.size > 0) {
        const masked: Record<string, unknown> = { ...partial };
        for (const f of claimed) delete masked[f];
        partial = masked as Partial<TelemetryEnvelope>;
      }
      const merged = mergeNonNull(prev, partial);
      this.lastTelemetry.set(update.droneId, merged);
      this.emit({ kind: 'telemetry', payload: merged });
    }

    // No OI is derived from this topic. `operational_volume` does ride
    // along in the payload, but the ticket maps the OI / flight plan to
    // `v1/output/aircraft/{sn}/oi/declaration` and the FLYsafe MQTT doc
    // places `oiPolygon[]` there and nowhere else. Until that topic is
    // enabled backend-side the OI context is simply empty — which is the
    // honest reflection of what the broker is publishing.

    // TIS-B conspicuity + agent link, replacing the dead `TISB Stream/`
    // and `traffic_control` topics. Both fields are named explicitly in
    // the ticket's telemetry row, so these are in scope.
    const c2 = outputTelemetryToC2Update(raw);
    if (c2) this.applyOutputC2(c2);

    // Mission metadata for the OI panel. `oi/declaration` doesn't carry
    // `main_flight_type` / `legal_profile` on any tenant — telemetry does.
    const oi = outputTelemetryToOiUpdate(raw);
    if (oi) this.applyOiPartial(oi.drone_id, oi.partial);
  }

  /** Content signature for OI dedupe — everything a consumer can see,
   *  minus `received_at`. networkid republishes the same mission metadata
   *  at 1.7 Hz; without this the OI envelope would refan on every tick,
   *  resyncing the map polygon layer and re-rendering the OI list for no
   *  visible change. */
  private static oiSignature(o: OiLiveEnvelope): string {
    return [
      o.oi_id,
      o.status,
      o.oi_ref_id,
      o.op_type,
      o.legal_rule,
      o.pilot_name,
      o.mission_name,
      o.mission_notes,
      o.mission_location,
      o.altitude_floor_m,
      o.altitude_ceiling_m,
      o.altitude_datum,
      o.agl_ceiling_ft,
      o.polygon ? JSON.stringify(o.polygon.coordinates) : null,
      o.waypoints ? o.waypoints.length : null,
    ].join('|');
  }

  /** Merge an OI partial into the per-drone snapshot.
   *
   *  Strictly a decorator: it requires an envelope that `oi/declaration`
   *  (via `handleFlightplan`) has already seeded. networkid carries mission
   *  metadata but no declared volume, and manufacturing an OI record for a
   *  drone that has not declared one would render a ghost OI in the list
   *  and on the map. */
  private applyOiPartial(droneId: string, partial: Record<string, unknown>): void {
    const prev = this.lastOi.get(droneId);
    if (!prev) return;

    const pick = <T>(key: string, fallback: T): T => (partial[key] as T | undefined) ?? fallback;

    const waypoints = pick<OiLiveEnvelope['waypoints']>('waypoints', prev?.waypoints ?? null);
    // Waypoint count is the only mission figure this namespace supports.
    // Leave the block null rather than emit a fake "0/N" that would shrink
    // the moment a real progress source lands.
    const mission =
      prev?.mission ??
      (waypoints && waypoints.length > 0
        ? {
            waypoints_total: waypoints.length,
            waypoints_done: 0,
            next_wp_id: null,
            dist_to_next_km: null,
          }
        : null);

    const merged: OiLiveEnvelope = {
      drone_id: droneId,
      oi_id: pick('oi_id', prev?.oi_id ?? droneId),
      status: prev?.status ?? 'Unknown',
      oi_ref_id: pick('oi_ref_id', prev?.oi_ref_id ?? null),
      ops_approval: prev?.ops_approval ?? null,
      // Published only on `oi/declaration`; this namespace never carries it,
      // so it is strictly carried forward and never cleared here.
      oir: prev?.oir ?? null,
      rtm_state: prev?.rtm_state ?? null,
      rtm_is_approved: prev?.rtm_is_approved ?? null,
      op_type: pick('op_type', prev?.op_type ?? null),
      legal_rule: pick('legal_rule', prev?.legal_rule ?? null),
      start_utc: prev?.start_utc ?? null,
      end_utc: prev?.end_utc ?? null,
      pilot_name: pick('pilot_name', prev?.pilot_name ?? null),
      last_assessment_at: prev?.last_assessment_at ?? null,
      subscribed_services: prev?.subscribed_services ?? [],
      mission,
      rth: prev?.rth ?? null,
      agl_ceiling_ft: pick('agl_ceiling_ft', prev?.agl_ceiling_ft ?? null),
      altitude_floor_m: pick('altitude_floor_m', prev?.altitude_floor_m ?? null),
      altitude_ceiling_m: pick('altitude_ceiling_m', prev?.altitude_ceiling_m ?? null),
      altitude_datum: pick('altitude_datum', prev?.altitude_datum ?? null),
      waypoints,
      mission_name: pick('mission_name', prev?.mission_name ?? null),
      mission_notes: pick('mission_notes', prev?.mission_notes ?? null),
      mission_location: pick('mission_location', prev?.mission_location ?? null),
      polygon: pick('polygon', prev?.polygon ?? null),
      received_at: new Date().toISOString(),
    };

    if (MqttSourceLegacy.oiSignature(prev) === MqttSourceLegacy.oiSignature(merged)) return;
    this.lastOi.set(droneId, merged);
    this.emit({ kind: 'oi', payload: merged });
  }

  /** TIS-B + aircraft-agent status from the telemetry topic.
   *
   *  Deduped for the same reason as OI: these flags change rarely but the
   *  topic republishes them at 1.7 Hz, and every c2 emit re-renders the
   *  Overview tab. */
  /**
   * Re-emit this drone's cached c2 envelope once, right after the drone
   * itself has been published.
   *
   * Fixes a lost update. `applyOutputC2` suppresses re-emits when nothing
   * changed (TIS-B status and agent state are near-constant), so in practice
   * it emits exactly once — on the first telemetry message. The drones store
   * drops a c2 envelope for a drone it doesn't know yet (`applyC2` returns
   * early when `byId[drone_id]` is missing), and `networkid` is what creates
   * the drone. Whenever telemetry wins that race, Overview's Aircraft Agent
   * and TIS-B tiles stay "—"/"N/A" for the whole session even though both
   * values were parsed. Replaying after the drone exists closes it.
   */
  private replayC2(droneId: string): void {
    if (this.c2Replayed.has(droneId)) return;
    const c2 = this.lastC2.get(droneId);
    if (!c2) return;
    this.c2Replayed.add(droneId);
    this.emit({ kind: 'c2links', payload: c2 });
  }

  private applyOutputC2(update: {
    drone_id: string;
    tisb_status: 'Active' | 'N/A';
    adapter_connected: boolean | null;
  }): void {
    const prev = this.lastC2.get(update.drone_id);
    const prevTisb = prev?.ec?.tisb?.status ?? null;
    // `adapter_connected` is NOT a source for the Aircraft Agent tile.
    //
    // It reads `true` on the wire and used to drive `aa_agent_status:
    // 'Online'`, but the equivalence is unfounded: the field appears nowhere
    // in the FLYsafe MQTT doc, and the doc documents no agent-status field on
    // any topic (`c2links` carries per-LINK metrics only — label, tech,
    // status, latency, rssi, rfScore, isLead, uplink, downlink). It is also
    // structurally unable to report the failure case: a dead agent stops
    // publishing, so the tile would latch on the last `true` rather than ever
    // showing 'Offline'.
    //
    // Left as `—` until the backend specifies where agent health lives.
    // The value is still carried on the envelope's `adapter_connected` for
    // whoever wires it to a real, documented consumer.
    if (prev && prevTisb === update.tisb_status) return;

    const base = prev ?? emptyC2(update.drone_id);
    const next: C2LinksEnvelope = {
      ...base,
      ec: {
        adsbOut: base.ec?.adsbOut ?? { status: 'N/A', freq: null, callsign: null },
        adsbIn: base.ec?.adsbIn ?? { status: 'N/A', freq: null },
        tisb: { status: update.tisb_status, towers: base.ec?.tisb?.towers ?? null },
      },
      received_at: new Date().toISOString(),
    };
    this.lastC2.set(update.drone_id, next);
    this.emit({ kind: 'c2links', payload: next });
  }

  /** Backfill: re-emit the most recent telemetry envelope for this drone
   *  with `status` / `status_label` patched in. Drops the message if we
   *  haven't seen any networkid telemetry for this drone yet — there's
   *  nothing to merge into. */
  private handleTrafficControl(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const update = trafficControlToUpdate(raw);
    if (!update) return;
    const prev = this.lastTelemetry.get(update.drone_id);
    if (!prev) return; // no telemetry yet — wait for the first networkid msg
    if (prev.status === update.status && prev.status_label === update.status_label) return;
    const merged: TelemetryEnvelope = {
      ...prev,
      status: update.status,
      status_label: update.status_label,
      received_at: new Date().toISOString(),
    };
    this.lastTelemetry.set(update.drone_id, merged);
    this.emit({ kind: 'telemetry', payload: merged });
  }

  /** Backfill: synthesise a minimal c2 envelope carrying just TIS-B
   *  status. The store treats this as a last-writer-wins replace, so we
   *  always include the previous snapshot's fields where we have them.
   *  Dedupes: if nothing about the c2 envelope visible to consumers has
   *  changed (only `received_at` would), we skip the emit. Otherwise the
   *  store applies a new envelope reference every second, every
   *  `useDronesStore` selector that reads `drone.c2` re-renders, and the
   *  Overview tab visibly thrashes. */
  private handleTisbStream(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const update = tisbStreamToUpdate(raw);
    if (!update) return;
    const prev = this.lastC2.get(update.drone_id);
    const prevTisbStatus = prev?.ec?.tisb?.status ?? null;
    if (prev && prevTisbStatus === update.tisb_status) return; // no visible change
    const base = prev ?? emptyC2(update.drone_id);
    const next: C2LinksEnvelope = {
      ...base,
      ec: {
        adsbOut: base.ec?.adsbOut ?? { status: 'N/A', freq: null, callsign: null },
        adsbIn: base.ec?.adsbIn ?? { status: 'N/A', freq: null },
        tisb: { status: update.tisb_status, towers: base.ec?.tisb?.towers ?? null },
      },
      received_at: new Date().toISOString(),
    };
    this.lastC2.set(update.drone_id, next);
    this.emit({ kind: 'c2links', payload: next });
  }

  /** v1 `flightplan/<UAS_ID>` — primary source for the OI polygon
   *  (`flight_volume`), pilot, legal rule, op type, schedule, and a
   *  static waypoint count. Low cadence (~1 msg per 7 s) — treated as a
   *  snapshot, merged into `lastOi` so future partial-update sources
   *  (live conformance, mission cursor) don't clobber the polygon.
   *
   *  Drops the message if the store doesn't know about this drone yet
   *  (no networkid baseline). The OI store applies on the drone-id key,
   *  and `applyOi` itself bails if the drone isn't tracked. */
  private handleFlightplan(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const update = flightplanToOiUpdate(raw);
    if (!update) return;
    const prev = this.lastOi.get(update.drone_id);
    // Merge partial into prior snapshot. We want a complete OiLiveEnvelope
    // for the emit — fill in anything the partial omitted from the prior
    // snapshot, then fall back to sensible defaults so Zod validates.
    const nowIso = new Date().toISOString();
    const merged: OiLiveEnvelope = {
      drone_id: update.drone_id,
      oi_id: update.partial.oi_id ?? prev?.oi_id ?? update.drone_id,
      // Latest published rtm_state wins; messages without one keep it (ENG-213).
      status: update.partial.status ?? prev?.status ?? 'Unknown',
      oi_ref_id: update.partial.oi_ref_id ?? prev?.oi_ref_id ?? null,
      ops_approval: update.partial.ops_approval ?? prev?.ops_approval ?? null,
      // The authority's operational-intent record. Carried forward from the
      // prior snapshot when a message omits it, so the legacy `flightplan/`
      // topic — which never carries it — can't blank what `oi/declaration`
      // already established for the same aircraft.
      oir: update.partial.oir ?? prev?.oir ?? null,
      rtm_state: update.partial.rtm_state ?? prev?.rtm_state ?? null,
      rtm_is_approved: update.partial.rtm_is_approved ?? prev?.rtm_is_approved ?? null,
      op_type: update.partial.op_type ?? prev?.op_type ?? null,
      legal_rule: update.partial.legal_rule ?? prev?.legal_rule ?? null,
      start_utc: update.partial.start_utc ?? prev?.start_utc ?? null,
      end_utc: update.partial.end_utc ?? prev?.end_utc ?? null,
      pilot_name: update.partial.pilot_name ?? prev?.pilot_name ?? null,
      last_assessment_at: prev?.last_assessment_at ?? null,
      subscribed_services: prev?.subscribed_services ?? [],
      mission: update.partial.mission ?? prev?.mission ?? null,
      rth: prev?.rth ?? null,
      agl_ceiling_ft: update.partial.agl_ceiling_ft ?? prev?.agl_ceiling_ft ?? null,
      altitude_floor_m: update.partial.altitude_floor_m ?? prev?.altitude_floor_m ?? null,
      altitude_ceiling_m: update.partial.altitude_ceiling_m ?? prev?.altitude_ceiling_m ?? null,
      altitude_datum: update.partial.altitude_datum ?? prev?.altitude_datum ?? null,
      mission_name: prev?.mission_name ?? null,
      mission_notes: prev?.mission_notes ?? null,
      mission_location: prev?.mission_location ?? null,
      polygon: update.partial.polygon ?? prev?.polygon ?? null,
      waypoints: prev?.waypoints ?? null,
      received_at: nowIso,
    };
    this.lastOi.set(update.drone_id, merged);
    this.emit({ kind: 'oi', payload: merged });
  }

  private handleSurveillanceTraffic(emitter: string, payload: Buffer) {
    // Defence-in-depth gate (broker-side subscription should already exclude
    // these, but a wildcard slip elsewhere would otherwise let live_community /
    // live_ads_count / metrics / stream leak in).
    if (!isServiceEmitter(emitter)) return;
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    // Topic-suffix `emitter` is canonical; the parser ignores any
    // abbreviated emitter field in the payload (e.g. `live_regional_edm`
    // vs `live_regional_edmonton`) so `source_services` tags match the
    // service ids used everywhere else.
    const result = surveillanceTrafficToPartialTargets(emitter, raw, new Date().toISOString());
    if (!result) return;
    const now = Date.now();
    this.trafficSeenAt.set(emitter, now);
    this.discoveredServices.add(emitter);
    // Store the partial keyed by emitter. The fused source_services list +
    // winning trust/position are computed at drain time in emitFusedTargets,
    // so an emitter going silent naturally drops out of both. Don't merge
    // shape here — that's what caused the trust flip-flop bug.
    for (const t of result.targets) {
      let entry = this.fusedTargets.get(t.target_id);
      if (!entry) {
        entry = { slots: new Map(), winner: null };
        this.fusedTargets.set(t.target_id, entry);
      }
      entry.slots.set(emitter, { partial: t, seenAt: now });
    }
  }

  /** `v1/ingest/live_rf/*` (ENG-253). REAL_TIME_DATA feeds both the target
   *  fusion map and the sensor snapshot for the `live_drone_rf` service;
   *  HEARTBEAT only keeps that service alive between frames. Snapshots are
   *  keyed per sensor SN so several sensors fold into one service. */
  private handleLiveRf(kind: string, payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const nowIso = new Date().toISOString();
    const now = Date.now();
    if (kind === 'HEARTBEAT') {
      const sn = parseLiveRfHeartbeat(raw);
      const entry = sn ? this.sensorSnapshots.get(`${LIVE_RF_EMITTER}:${sn}`) : undefined;
      if (!sn || !entry) return;
      const stations = entry.snapshot.stations.map((s) =>
        s.state === 'live' ? { ...s, last_seen: nowIso } : s,
      );
      this.sensorSnapshots.set(`${LIVE_RF_EMITTER}:${sn}`, {
        snapshot: { ...entry.snapshot, stations, receivedAtIso: nowIso },
        seenAt: now,
      });
      return;
    }
    if (kind !== 'REAL_TIME_DATA') return;
    const frame = parseLiveRfRealtime(raw, nowIso);
    if (!frame) return;
    this.sensorSnapshots.set(`${LIVE_RF_EMITTER}:${frame.sensorSn}`, {
      snapshot: frame.snapshot,
      seenAt: now,
    });
    this.discoveredServices.add(frame.snapshot.service_id);
    for (const t of frame.targets) {
      let entry = this.fusedTargets.get(t.target_id);
      if (!entry) {
        entry = { slots: new Map(), winner: null };
        this.fusedTargets.set(t.target_id, entry);
      }
      entry.slots.set(LIVE_RF_EMITTER, { partial: t, seenAt: now });
    }
  }

  /** v1 `surveillance_sensors/<emitter>` — parse + stash a per-emitter
   *  snapshot. The unified `surveillance` envelope is emitted on a
   *  timer (5 s) rather than per-message so the store doesn't get
   *  hammered when multiple emitters publish in the same window. */
  private handleSurveillanceSensors(emitter: string, payload: Buffer) {
    if (!isServiceEmitter(emitter)) return;
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const snapshot = parseSurveillanceSensors(emitter, raw);
    if (!snapshot) return;
    this.sensorSnapshots.set(emitter, { snapshot, seenAt: Date.now() });
    this.noteService(snapshot.service_id);
  }

  /** A surveillance service has been seen on a sensors topic: remember it, and
   *  make sure its traffic topic is subscribed. This is how services the app has
   *  no code for (a new regional, say) still show up with their targets. */
  private noteService(id: string) {
    this.discoveredServices.add(id);
    if ((KNOWN_EMITTERS as readonly string[]).includes(id)) return; // subscribed up front
    if (this.dynamicTrafficSubs.has(id) || !this.client) return;
    this.dynamicTrafficSubs.add(id);
    this.subscribeTopic(this.client, `surveillance_traffic/${id}`);
  }

  /** Drain the per-emitter snapshot map into a unified v2
   *  SurveillanceEnvelope. Evicts emitters silent for longer than
   *  SURVEILLANCE_STALE_MS so the service counts don't lag forever
   *  after a sensor backend goes offline. */
  private emitSurveillance() {
    if (this.listeners.size === 0) return;
    const now = Date.now();
    for (const [emitter, entry] of this.sensorSnapshots) {
      if (now - entry.seenAt > SURVEILLANCE_STALE_MS) {
        this.sensorSnapshots.delete(emitter);
      }
    }
    const snapshots = [...this.sensorSnapshots.values()].map((e) => e.snapshot);
    // Cold-start cutoff: services with no snapshot but still within
    // this window show 'Connecting'; past it, they flip to 'Offline'.
    const connectingUntilMs =
      this.mqttConnectedAtMs != null ? this.mqttConnectedAtMs + SURVEILLANCE_CONNECTING_MS : 0;
    const envelope = snapshotsToEnvelope(
      snapshots,
      connectingUntilMs,
      this.discoveredServices,
      this.trafficLive(),
      this.visibleByService(),
    );
    // Content-dedupe: most 5 s ticks have no actual change because v1's
    // sensor emitters publish at 30-90 s cadences. Without this guard
    // every tick replaces useSurvStore.byId with a fresh-reference
    // identical-content object, which re-triggers the map sensor layer
    // sync, the SurvList rerender, and the per-drone Surveillance tab.
    // Signature includes the per-service counts + station-id set so any
    // real change (sensor going offline, regional dropping a peer)
    // still emits, but cosmetic re-emits stop.
    const sig = envelope.services
      .map(
        (s) =>
          `${s.service_id}=${s.online_sensors}/${s.total_sensors}:${s.service_state}:${s.stations.map((st) => st.sensor_id).join(',')}`,
      )
      .join('|');
    if (sig === this.lastSurveillanceSig) return;
    this.lastSurveillanceSig = sig;
    this.emit({ kind: 'surveillance', payload: envelope });
  }

  /** Apply a MAVLink subtype update to the per-drone telemetry snapshot
   *  and schedule a flush. Drops the message if there's no networkid
   *  baseline yet — MAVLink alone doesn't carry identity (callsign,
   *  drone_model, drone_type), so we wait for the first networkid msg
   *  to seed the envelope. */
  /** Parse the flysafe/v1 topic path, extract tenant + drone + producer,
   *  stamp them onto the drone's c2 envelope. Ticket 86e1hnwf5 item 1.
   *
   *  Topic shape (sniff-verified):
   *    flysafe/v1/tenant/<tenant>/aircraft/<drone_id>/producer/<producer_id>/telemetry
   *
   *  The GCS counterpart (`...gcs/...`) hasn't been seen on staging yet
   *  (zero matching topics in 90 s sniff 2026-05-26). When the broker
   *  starts publishing it, the same parser handles it. */
  private handleAgentProducer(topic: string) {
    const m = topic.match(
      /^flysafe\/v1\/tenant\/([^/]+)\/(aircraft|gcs)\/([^/]+)\/producer\/(.+?)\/telemetry$/,
    );
    if (!m) return;
    const tenant = m[1]!;
    const role = m[2]! as 'aircraft' | 'gcs';
    const droneId = m[3]!;
    const producerId = m[4]!;

    const now = Date.now();
    const nowIso = new Date(now).toISOString();

    // Throttle to ~4 Hz max — MAVLink subtype messages arrive much faster.
    const lastEmit = this.lastAgentEmitMs.get(droneId) ?? 0;
    const shouldEmit = now - lastEmit >= 250;

    // Rate calculation: keep 5 s window per drone × role.
    const windowKey = `${droneId}|${role}`;
    let win = this.agentMsgWindow.get(windowKey);
    if (!win) {
      win = [];
      this.agentMsgWindow.set(windowKey, win);
    }
    win.push(now);
    const cutoff = now - 5_000;
    while (win.length > 0 && win[0]! < cutoff) win.shift();
    const msgRate = win.length / 5;

    const prev = this.lastC2.get(droneId) ?? emptyC2(droneId);
    const next: C2LinksEnvelope = {
      ...prev,
      received_at: nowIso,
      ...(role === 'aircraft'
        ? {
            aa_agent_status: 'Online' as const,
            aa_producer_id: producerId,
            aa_tenant: tenant,
            aa_last_msg_at: nowIso,
            aa_msg_rate: Math.round(msgRate * 10) / 10,
          }
        : {
            gcs_agent_status: 'Online' as const,
            gcs_producer_id: producerId,
            gcs_tenant: tenant,
            gcs_last_msg_at: nowIso,
            gcs_msg_rate: Math.round(msgRate * 10) / 10,
          }),
    };
    this.lastC2.set(droneId, next);
    if (shouldEmit) {
      this.lastAgentEmitMs.set(droneId, now);
      this.emit({ kind: 'c2links', payload: next });
    }
  }

  /** Fields the output topics must leave alone for `droneId`: whatever the legacy
   *  MAVLink path claimed, plus what the raw GCS stream claimed. The stream's
   *  claim lapses after GCS_STALE_MS of silence, so a stalled stream doesn't pin
   *  the fields and mask the output topics indefinitely. */
  private claimedFor(droneId: string): Set<string> | undefined {
    const legacy = this.mavlinkClaimed.get(droneId);
    const gcs = this.gcsClaimed.get(droneId);
    if (gcs && Date.now() - this.gcsLastMsgMs > GCS_STALE_MS) {
      this.gcsClaimed.delete(droneId);
      return legacy;
    }
    if (!gcs || gcs.size === 0) return legacy;
    if (!legacy || legacy.size === 0) return gcs;
    return new Set([...legacy, ...gcs]);
  }

  /** Follow the selected aircraft: subscribe its raw GCS stream and drop the
   *  previous one. `null` stops the stream. Only the `v1/output` schema has the
   *  topic, so on a legacy-schema tenant this just records the focus. */
  setFocusedAircraft(serial: string | null): void {
    if (serial === this.focusedAircraft) return;
    const previous = this.focusedAircraft;
    this.focusedAircraft = serial;
    this.gcsTypeLastMs.clear();
    if (previous) {
      // Hand the fields back: the stream no longer owns them, and its last
      // readings must not be re-emitted as if they were live.
      this.gcsClaimed.delete(previous);
      const last = this.lastTelemetry.get(previous);
      if (last) {
        const cleared: Record<string, unknown> = { ...last };
        for (const f of GCS_STREAM_FIELDS) cleared[f] = null;
        this.lastTelemetry.set(previous, cleared as TelemetryEnvelope);
      }
      // Unsubscribe whenever there is a client, even mid-reconnect: mqtt.js drops
      // the topic from its resubscribe list, so it can't come back on reconnect.
      if (this.client && this.gcsStreamAvailable()) {
        this.client.unsubscribe(gcsStreamTopic(previous));
      }
    }
    // While disconnected the connect handler subscribes the focused aircraft.
    if (serial && this.client && this.state === 'connected' && this.gcsStreamAvailable()) {
      this.subscribeTopic(this.client, gcsStreamTopic(serial));
    }
  }

  /** The raw GCS stream lives in the `v1/input` namespace, which only exists on
   *  tenants that publish the `v1/output` schema. */
  private gcsStreamAvailable(): boolean {
    return config.mqtt.schema !== 'legacy';
  }

  /** `v1/input/aircraft/{sn}/gcs_telemetry` — satellites, HDOP, GPS fix,
   *  airspeed, attitude and pressure for the selected aircraft. */
  private handleGcsStream(payload: Buffer) {
    // Cheap pre-filter: skip types we don't read, and copies of a type arriving
    // faster than the UI flushes, before paying for JSON.parse + validation.
    const text = payload.toString();
    const type = peekGcsType(text);
    if (!type || !GCS_READ_TYPES.has(type)) return;
    const now = Date.now();
    const last = this.gcsTypeLastMs.get(type);
    if (last !== undefined && now - last < GCS_TYPE_MIN_GAP_MS) {
      // Still proof the stream is alive, so the claim must not lapse.
      this.gcsLastMsgMs = now;
      return;
    }
    this.gcsTypeLastMs.set(type, now);
    let raw: unknown;
    try {
      raw = JSON.parse(text);
    } catch {
      return;
    }
    const update = gcsStreamToPartial(raw);
    if (!update) return;
    // A message still in flight after the selection moved on.
    if (update.droneId !== this.focusedAircraft) return;
    const prev = this.lastTelemetry.get(update.droneId);
    if (!prev) return; // wait for the networkid baseline
    this.lastTelemetry.set(update.droneId, mergeNonNull(prev, update.partial));
    // Claim what the stream supplied so the 1 Hz output topics stop overwriting
    // it with their guesses (HDOP and GPS lock from position accuracy).
    let claimed = this.gcsClaimed.get(update.droneId);
    if (!claimed) {
      claimed = new Set<string>();
      this.gcsClaimed.set(update.droneId, claimed);
    }
    for (const [k, v] of Object.entries(update.partial)) {
      if (v !== null && v !== undefined) claimed.add(k);
    }
    this.gcsLastMsgMs = now;
    this.mavlinkDirty.add(update.droneId);
    this.scheduleMavlinkFlush();
  }

  private handleMavlink(payload: Buffer) {
    let raw: unknown;
    try {
      raw = JSON.parse(payload.toString());
    } catch {
      return;
    }
    const update = mavlinkToPartialTelemetry(raw);
    if (!update) return;
    const prev = this.lastTelemetry.get(update.droneId);
    if (!prev) return; // wait for networkid baseline
    const merged = mergeNonNull(prev, update.partial);
    this.lastTelemetry.set(update.droneId, merged);
    // Claim every field this patch actually supplied a non-null value
    // for. handleNetworkid then yields to MAVLink on those fields so the
    // two sources stop fighting at 1 Hz (see mavlinkClaimed comment).
    let claimed = this.mavlinkClaimed.get(update.droneId);
    if (!claimed) {
      claimed = new Set<string>();
      this.mavlinkClaimed.set(update.droneId, claimed);
    }
    for (const [k, v] of Object.entries(update.partial)) {
      if (v !== null && v !== undefined) claimed.add(k);
    }
    this.mavlinkDirty.add(update.droneId);
    this.scheduleMavlinkFlush();
  }

  /** Emit the current snapshot for every drone with pending updates.
   *  Coalesces multiple subtype messages into one envelope per flush. */
  private scheduleMavlinkFlush() {
    if (this.mavlinkFlushTimer != null) return;
    this.mavlinkFlushTimer = window.setTimeout(() => {
      this.mavlinkFlushTimer = null;
      for (const id of this.mavlinkDirty) {
        const env = this.lastTelemetry.get(id);
        if (env) this.emit({ kind: 'telemetry', payload: env });
      }
      this.mavlinkDirty.clear();
    }, MAVLINK_FLUSH_MS);
  }

  /** Build a unified targets envelope from the fusion table and emit.
   *
   *  Sticky-winner model. For each target:
   *    1. Drop emitter slots silent > TARGET_STALE_MS.
   *    2. If no emitters survive, drop the whole target.
   *    3. If the current winner is still alive AND no surviving emitter
   *       has strictly higher trust, keep it. This is the sticky bit —
   *       same plane, same emitter, no per-tick flipping when multiple
   *       same-trust emitters disagree on type / position / gs units.
   *    4. Otherwise pick a new winner: highest trust, tiebreak by stable
   *       EMITTER_PRIORITY order (live_ads > live_mlat > live_vision >
   *       live_regional_* > live_community). Deterministic.
   *    5. The winner's `partial` supplies position / heading / speed /
   *       altitude / type / trust. `source_services` = every surviving
   *       emitter id, so the "N src" label matches the set of feeds
   *       currently reporting this aircraft. */
  private emitFusedTargets() {
    if (this.listeners.size === 0) return;
    const now = Date.now();
    const targets: Target[] = [];
    for (const [key, entry] of this.fusedTargets) {
      // 1. Prune stale per-emitter slots in place.
      for (const [emitter, slot] of entry.slots) {
        if (now - slot.seenAt > TARGET_STALE_MS) entry.slots.delete(emitter);
      }
      // 2. No surviving emitter → target itself is gone.
      if (entry.slots.size === 0) {
        this.fusedTargets.delete(key);
        continue;
      }
      // 3/4. Choose a winning emitter. Sticky unless promotion or eviction.
      let winnerEmitter = entry.winner;
      const currentWinner = winnerEmitter ? entry.slots.get(winnerEmitter) : undefined;
      const needsRepick =
        !winnerEmitter ||
        !currentWinner ||
        hasHigherTrust(entry.slots, currentWinner.partial.trust);
      if (needsRepick) winnerEmitter = pickEmitter(entry.slots);
      entry.winner = winnerEmitter;
      if (!winnerEmitter) continue;
      const winnerSlot = entry.slots.get(winnerEmitter);
      if (!winnerSlot) continue;
      // 5. source_services = every surviving emitter key.
      const sourceServices = [...entry.slots.keys()];
      // dist_km / vert_sep_ft / horiz_sep_nm / proximity left null —
      // computed downstream (targets store + map markers already do this
      // against the selected drone).
      targets.push({
        ...winnerSlot.partial,
        source_services: sourceServices,
        dist_km: null,
        vert_sep_ft: null,
        horiz_sep_nm: null,
        proximity: 'safe',
      });
    }
    if (targets.length === 0) return;
    const env: TargetsEnvelope = {
      targets,
      received_at: new Date().toISOString(),
    };
    this.emit({ kind: 'targets', payload: env });

    // Optional diagnostic — set `window.__DEBUG_TARGETS__ = 'CFOLE'` (or any
    // callsign) in DevTools to dump per-emitter slots for that target every
    // drain. Lets us see if live_ads / live_mlat / live_vision disagree on
    // category, altitude, gs unit, lat/lng — the root cause of any residual
    // visual flicker. Off by default; zero cost when unset.
    const debugId =
      typeof window !== 'undefined'
        ? (window as unknown as { __DEBUG_TARGETS__?: string }).__DEBUG_TARGETS__
        : undefined;
    if (debugId) {
      const entry = this.fusedTargets.get(debugId);
      if (entry) {
        const rows = [...entry.slots].map(([em, s]) => ({
          em,
          age_ms: now - s.seenAt,
          type: s.partial.type,
          cat: s.partial.source_services,
          alt_ft: s.partial.altitude_ft,
          gs: s.partial.speed_kts,
          hdg: Math.round(s.partial.heading),
          lat: +s.partial.lat.toFixed(5),
          lng: +s.partial.lng.toFixed(5),
        }));
        // eslint-disable-next-line no-console
        console.table({
          winner: entry.winner,
          ...Object.fromEntries(rows.map((r, i) => [`slot_${i}`, r])),
        });
      }
    }
  }
}

// The MQTT source is a singleton on `globalThis` (source.ts) that survives Vite
// HMR and subscribes exactly once, when it connects. A tab that was open while
// this class (its routing, or the subscription list) changed therefore keeps the
// OLD subscriptions and the old class code — new topics simply never arrive, which
// the Debug panel shows as empty rows (seen 2026-10-07 for the waypoint topics).
// Only a fresh connection fixes that, so reload instead of hot-swapping.
if (import.meta.hot) {
  import.meta.hot.accept(() => window.location.reload());
}
